/**
 * Suggestion popover for the Keyword search input (row 0 of KeywordSearchForm).
 *
 * - ``useSearchSuggest``: 150ms debounce, one in-flight request at a time
 *   (AbortController cancels the previous request on every keystroke / region
 *   change — a stale response for text the user has moved past must never
 *   land). Rows from an older query/regions are never returned. Errors resolve to no rows so the popover degrades to the
 *   "Search labels" row only.
 * - ``buildSuggestOptions``: Label row → top 3 rows per kind in backend order,
 *   groups in KIND_LABELS order, empty kinds omitted; field row → every row
 *   of that row's kind, no headers.
 * - A pick sets the focused row's type and value (patterns use wildcards).
 */
import React, { useEffect, useRef, useState } from 'react';
import { authApi } from '../services/authApi';
import type { ChipKind, SuggestRow } from '../types';
import { KIND_LABELS } from './SearchChipBar';

const DEBOUNCE_MS = 150;
const PER_KIND_CAP = 3;

export interface SuggestState {
  rows: SuggestRow[];
  status: 'idle' | 'loading' | 'done';
}

const IDLE: SuggestState = { rows: [], status: 'idle' };

export function useSearchSuggest(q: string, regions?: string[]): SuggestState {
  // Rows are tagged with the query+regions they answer; anything else is
  // dropped, so a stale row can never be shown or picked while the next
  // request is pending.
  const key = JSON.stringify([q, regions ?? null]);
  const [state, setState] = useState<SuggestState & { key: string }>({ ...IDLE, key });
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Cancel any in-flight request immediately, not only when the next
    // debounce fires.
    abortRef.current?.abort();
    if (!q) {
      setState({ ...IDLE, key });
      return;
    }
    const timer = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;
      setState((s) => (s.key === key ? { ...s, status: 'loading' } : { rows: [], status: 'loading', key }));
      authApi
        .searchSuggest(q, {
          limit: 10,
          signal: controller.signal,
          ...(regions ? { regions } : {}),
        })
        .then((rows) => {
          if (!controller.signal.aborted) setState({ rows, status: 'done', key });
        })
        .catch(() => {
          // Aborted: a newer request owns the state. Real error: no rows, the
          // label search is never blocked.
          if (!controller.signal.aborted) setState({ rows: [], status: 'done', key });
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q, regions]); // eslint-disable-line react-hooks/exhaustive-deps -- key derives from both

  // Cancel on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  return state.key === key ? { rows: state.rows, status: state.status } : IDLE;
}

export interface SuggestGroup {
  kind: ChipKind | null;
  rows: SuggestRow[];
}

const KIND_ORDER = Object.keys(KIND_LABELS) as ChipKind[];

export function buildSuggestOptions(rows: SuggestRow[], kind: ChipKind | null): SuggestGroup[] {
  if (kind) return [{ kind: null, rows: rows.filter((r) => r.kind === kind) }];
  return KIND_ORDER
    .map((k) => ({ kind: k, rows: rows.filter((r) => r.kind === k).slice(0, PER_KIND_CAP) }))
    .filter((g) => g.rows.length > 0);
}

interface KeywordSuggestPopoverProps {
  text: string;
  /** Scope kind, or null for All. */
  kind: ChipKind | null;
  groups: SuggestGroup[];
  status: SuggestState['status'];
  /** Index into the flat option list (All: 0 = "Search labels"); -1 = none. */
  highlight: number;
  onPickLabels: () => void;
  onPickRow: (row: SuggestRow) => void;
}

const optionClass = (active: boolean) =>
  `w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 flex items-center justify-between gap-2 ${
    active ? 'bg-indigo-50' : ''
  }`;

const KeywordSuggestPopover: React.FC<KeywordSuggestPopoverProps> = ({
  text, kind, groups, status, highlight, onPickLabels, onPickRow,
}) => {
  const all = kind === null;
  const hasRows = groups.some((g) => g.rows.length > 0);
  let flat = all ? 1 : 0; // flat index of the next suggestion row

  return (
    <ul
      role="listbox"
      aria-label="Suggestions"
      data-testid="search-chip-suggestions"
      className="absolute z-20 left-0 right-0 top-full mt-1 max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg"
    >
      {all && (
        <li role="presentation">
          <button
            type="button"
            role="option"
            aria-selected={highlight === 0}
            data-testid="suggest-search-labels"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onPickLabels}
            className={optionClass(highlight === 0)}
          >
            Search labels for &quot;{text}&quot;
          </button>
        </li>
      )}
      {groups.map((g, gi) => (
        <li
          key={g.kind ?? gi}
          role="group"
          {...(g.kind ? { 'aria-label': KIND_LABELS[g.kind] } : {})}
        >
          {g.kind && (
            <div
              data-testid={`suggest-group-${g.kind}`}
              className="px-3 pt-1.5 pb-0.5 text-xs font-semibold text-slate-400"
            >
              {KIND_LABELS[g.kind]}
            </div>
          )}
          <ul role="presentation">
            {g.rows.map((row, idx) => {
              const i = flat++;
              return (
                <li key={`${row.kind}-${row.value}-${row.pattern ? 'p' : 'e'}-${idx}`} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={highlight === i}
                    data-testid="search-chip-suggestion"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onPickRow(row)}
                    className={optionClass(highlight === i)}
                  >
                    <span>
                      <span className="text-slate-400 mr-1.5">{KIND_LABELS[row.kind]}</span>
                      {row.label}
                      {row.pattern && <span className="ml-1 text-indigo-400">(pattern)</span>}
                    </span>
                    {row.drug_count != null && (
                      <span className="text-slate-400 text-xs">{row.drug_count}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </li>
      ))}
      {!all && !hasRows && status === 'done' && (
        <li role="presentation" className="px-3 py-1.5 text-sm text-slate-400">
          No {KIND_LABELS[kind]} matches
        </li>
      )}
    </ul>
  );
};

export default KeywordSuggestPopover;
